/**
 * The signed consent request an assistant's authorization hands to this app.
 *
 * The MCP server cannot ask the person who they are: a browser navigation
 * carries no `Authorization` header, and the API's session cookie is
 * `__Host-` prefixed, so it is bound to the API origin and invisible to any
 * other host. So the server bounces the browser here — to the one origin that
 * already holds the session — with the request signed and in the URL.
 *
 * Two ordering facts shape this module.
 *
 * First, the capture has to happen at module load, not at render. If the person
 * is signed out they are about to leave for an identity provider and come back
 * to a bare URL; by then the parameter is gone. So it is read once, as early as
 * the module graph allows, and kept in `sessionStorage` — same tab, cleared when
 * the tab closes, never shared with another.
 *
 * Second, the parameter is stripped from the address bar immediately. It is not
 * a secret — it carries the client, the redirect, the scopes and the PKCE
 * challenge, all of which the client itself put in the authorize URL, and it is
 * HMAC-signed so it cannot be edited — but leaving it in place means a refresh
 * silently re-enters a flow the person may already have finished.
 */

/** Must match `CONSENT_BRIDGE_PARAM` in `apps/mcp/src/oauth/service.ts`. */
export const CONSENT_REQUEST_PARAM = "mcp_consent";

const STORAGE_KEY = "obsina.mcp.consent_request";

/** The bounds a signed request has to fit; anything else is not one of ours. */
const MIN_LENGTH = 16;
const MAX_LENGTH = 8192;
const SHAPE = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/u;

function looksLikeConsentRequest(value: string): boolean {
  return (
    value.length >= MIN_LENGTH &&
    value.length <= MAX_LENGTH &&
    SHAPE.test(value)
  );
}

function sessionStore(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    // Storage can throw outright when the browser blocks it. A connection that
    // cannot be resumed is better than one that crashes the app it lives in.
    return null;
  }
}

/**
 * Reads the parameter out of the current URL, stores it, and removes it from
 * the address bar. Safe to call more than once: a URL with no parameter leaves
 * whatever was already stored untouched.
 */
export function captureConsentRequestFromUrl(): string | null {
  if (typeof window === "undefined") return null;
  let raw: string | null;
  try {
    raw = new URLSearchParams(window.location.search).get(
      CONSENT_REQUEST_PARAM,
    );
  } catch {
    return null;
  }
  if (raw === null) return null;
  const stripped = new URL(window.location.href);
  stripped.searchParams.delete(CONSENT_REQUEST_PARAM);
  try {
    window.history.replaceState(
      window.history.state,
      "",
      `${stripped.pathname}${stripped.search}${stripped.hash}`,
    );
  } catch {
    // A blocked history write is cosmetic; the flow still works.
  }
  if (!looksLikeConsentRequest(raw)) return null;
  sessionStore()?.setItem(STORAGE_KEY, raw);
  return raw;
}

/** The pending request, if this tab is in the middle of connecting one. */
export function readConsentRequest(): string | null {
  const stored = sessionStore()?.getItem(STORAGE_KEY) ?? null;
  if (stored === null) return null;
  return looksLikeConsentRequest(stored) ? stored : null;
}

/** Ends the flow: called once the decision has been sent, either way. */
export function clearConsentRequest(): void {
  sessionStore()?.removeItem(STORAGE_KEY);
}

// Runs when the module is first imported, which the app's own import graph
// guarantees is before React renders anything — and therefore before a signed
// out person is sent away to sign in. Rendering is too late; see above.
captureConsentRequestFromUrl();
